/**
 * Datos de facturación alternativos de un cliente.
 *
 * Sirve para el caso de facturar a otro CUIT/CUIL del que tiene la ficha: el
 * auto lo trae la persona pero la factura va a su empresa, o al revés. Cada
 * perfil es un destinatario fiscal completo, no un campo suelto: mezclar el
 * documento de uno con la condición de otro daría comprobantes inconsistentes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, TrashIcon, PencilIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tip } from './ui/tip';
import { confirmDialog } from './ConfirmDialog';

export interface BillingProfile {
  id: string;
  clientId: string;
  nombre: string;
  docType: 'CUIT' | 'CUIL' | 'DNI';
  docNumber: string;
  ivaCondition: string;
  address?: string | null;
  isDefault?: number;
}

export const CONDICIONES: Array<{ value: string; label: string }> = [
  { value: 'responsable_inscripto', label: 'Responsable Inscripto' },
  { value: 'monotributista', label: 'Monotributista' },
  { value: 'exento', label: 'Exento' },
  { value: 'consumidor_final', label: 'Consumidor Final' },
  { value: 'no_responsable', label: 'No Responsable' }
];

export const etiquetaCondicion = (v?: string | null) =>
  CONDICIONES.find((c) => c.value === v)?.label || 'Consumidor Final';

/** Formatea 30712345678 como 30-71234567-8 para que se pueda leer. */
export const formatearDoc = (doc?: string | null, tipo?: string) => {
  const d = String(doc || '').replace(/\D/g, '');
  if (tipo === 'DNI' || d.length <= 8) return d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : d;
};

const VACIO = { nombre: '', docType: 'CUIT' as const, docNumber: '', ivaCondition: 'responsable_inscripto', address: '' };

export function BillingProfiles({ clientId }: { clientId: string }) {
  const [perfiles, setPerfiles] = useState<BillingProfile[]>([]);
  const [form, setForm] = useState<any>(null); // null = form cerrado
  const [guardando, setGuardando] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const cargar = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`/api/clients/${clientId}/billing-profiles`);
      setPerfiles(res.data.profiles || []);
    } catch { setPerfiles([]); }
  }, [clientId]);

  useEffect(() => { cargar(); }, [cargar]);

  /**
   * Trae razón social, condición de IVA y domicilio del padrón de ARCA.
   * Es la única forma de garantizar que el nombre que sale en la factura sea
   * el del titular de ese CUIT y no el del cliente que trajo el auto.
   */
  const traerDeAfip = async () => {
    const doc = String(form.docNumber || '').replace(/\D/g, '');
    if (doc.length !== 11) {
      toast.error('Para consultar en ARCA cargá el CUIT/CUIL completo (11 dígitos)');
      return;
    }
    setBuscando(true);
    try {
      const res = await axios.post('/api/clients/search-afip', { document: doc });
      const d = res.data?.data || {};
      const nombre = String(d.companyName || d.razonSocial || '').trim()
        || [d.firstName || d.nombre, d.lastName || d.apellido].filter(Boolean).join(' ').trim();
      if (!nombre) {
        toast.error('ARCA no devolvió el nombre de ese documento: cargalo a mano');
        return;
      }
      // El padrón devuelve el domicilio partido en campos
      const a = d.address;
      const domicilio = typeof a === 'string' ? a : [
        [a?.street, a?.streetNumber].filter(Boolean).join(' '),
        [a?.postalCode, a?.city, a?.province].filter(Boolean).join(', ')
      ].filter(Boolean).join(' - ');
      setForm((f: any) => ({
        ...f,
        nombre,
        ivaCondition: d.ivaCondition || d.taxCondition || f.ivaCondition,
        address: f.address || domicilio || ''
      }));
      // El padrón A13 responde por cualquier CUIT/CUIL pero no certifica la
      // condición de IVA: si no está inscripto lo devuelve como consumidor
      // final, y de eso depende que la factura salga A o B.
      if (res.data?.fuente === 'padron_a13') {
        toast.success(`${nombre}: no figura como inscripto, queda Consumidor Final. Verificá antes de facturar.`, { duration: 8000 });
      } else {
        toast.success('Datos traídos de ARCA');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo consultar ARCA: cargá los datos a mano');
    } finally { setBuscando(false); }
  };

  const guardar = async () => {
    if (!form.docNumber.trim()) {
      toast.error('Cargá el documento (CUIT, CUIL o DNI)');
      return;
    }
    // Sin razón social la factura sale con el nombre del cliente al lado de un
    // documento que no es suyo.
    if (!form.nombre.trim()) {
      toast.error('Cargá la razón social: es el nombre que va a salir en la factura');
      return;
    }
    setGuardando(true);
    try {
      if (form.id) await axios.put(`/api/clients/${clientId}/billing-profiles/${form.id}`, form);
      else await axios.post(`/api/clients/${clientId}/billing-profiles`, form);
      setForm(null);
      await cargar();
      toast.success('Datos de facturación guardados');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  const borrar = async (p: BillingProfile) => {
    if (!(await confirmDialog({
      title: 'Borrar estos datos de facturación',
      description: `${p.nombre || formatearDoc(p.docNumber, p.docType)} dejará de estar disponible al facturar. Las facturas ya emitidas no se tocan.`,
      confirmText: 'Borrar'
    }))) return;
    try {
      await axios.delete(`/api/clients/${clientId}/billing-profiles/${p.id}`);
      await cargar();
    } catch { toast.error('No se pudo borrar'); }
  };

  const marcarDefecto = async (p: BillingProfile) => {
    try {
      await axios.put(`/api/clients/${clientId}/billing-profiles/${p.id}`, { isDefault: true });
      await cargar();
    } catch { toast.error('No se pudo marcar'); }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Datos de facturación alternativos</p>
          <p className="text-xs text-muted-foreground">
            Para facturar a otro CUIT/CUIL. Si no cargás ninguno, la factura sale con los datos de arriba.
          </p>
        </div>
        {!form && (
          <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={() => setForm({ ...VACIO })}>
            <PlusIcon className="mr-1 h-4 w-4" />
            Agregar
          </Button>
        )}
      </div>

      {perfiles.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {perfiles.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {p.nombre || '(sin razón social)'}
                  {Number(p.isDefault) === 1 && (
                    <span className="ml-2 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                      por defecto
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {p.docType} {formatearDoc(p.docNumber, p.docType)} · {etiquetaCondicion(p.ivaCondition)}
                  {p.address ? ` · ${p.address}` : ''}
                </p>
              </div>
              {Number(p.isDefault) !== 1 && (
                <Tip label="Usar estos datos por defecto al facturar">
                  <button type="button" onClick={() => marcarDefecto(p)}
                    className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                    <CheckCircleIcon className="h-4 w-4" />
                  </button>
                </Tip>
              )}
              <Tip label="Editar">
                <button type="button" onClick={() => setForm({ ...p })}
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                  <PencilIcon className="h-4 w-4" />
                </button>
              </Tip>
              <Tip label="Borrar">
                <button type="button" onClick={() => borrar(p)}
                  className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </Tip>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <select value={form.docType} onChange={(e) => setForm({ ...form, docType: e.target.value })}
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                <option value="CUIT">CUIT</option>
                <option value="CUIL">CUIL</option>
                <option value="DNI">DNI</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Número</Label>
              <div className="flex items-center gap-2">
                <Input inputMode="numeric" autoComplete="off" value={form.docNumber}
                  onChange={(e) => setForm({ ...form, docNumber: e.target.value })}
                  placeholder="30-71234567-8" className="h-9 flex-1" />
                {form.docType !== 'DNI' && (
                  <Tip label="Traer razón social y domicilio del padrón de ARCA">
                    <Button type="button" variant="outline" size="sm" className="h-9 shrink-0"
                      onClick={traerDeAfip} disabled={buscando}>
                      {buscando ? 'Buscando...' : 'ARCA'}
                    </Button>
                  </Tip>
                )}
              </div>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Razón social o nombre</Label>
              <Input value={form.nombre} autoComplete="off" onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Transportes XX SRL" className="h-9" />
              <p className="text-[11px] text-muted-foreground">
                Es el nombre que sale impreso en la factura junto a ese documento. Tiene que ser el titular real, no el cliente que trae el auto.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Condición IVA</Label>
              <select value={form.ivaCondition} onChange={(e) => setForm({ ...form, ivaCondition: e.target.value })}
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                {CONDICIONES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Domicilio (opcional)</Label>
              <Input value={form.address || ''} autoComplete="off" onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Si se deja vacío usa el del cliente" className="h-9" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setForm(null)}>Cancelar</Button>
            <Button type="button" size="sm" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingProfiles;
